<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ConversationApi from 'dashboard/api/inbox/conversation';
import { previaIndentada } from 'dashboard/helper/agentTrace';

/**
 * O rastro de execucao do agente nesta conversa.
 *
 * Ele e do TURNO, nao da mensagem: `poweragents.turns` nao tem `message_id`, e um turno vira N
 * mensagens. Por isso o painel abre no turno mais PROVAVEL desta resposta — o ultimo que
 * comecou antes dela — e diz na tela que o casamento e por horario. Fingir precisao que o dado
 * nao tem seria pior que admitir a aproximacao, e os outros turnos ficam listados logo abaixo
 * para quem precisar conferir.
 *
 * O corte por papel e do SERVIDOR (`PowerAgents::TraceProjection`): quem nao e super admin
 * recebe um payload sem `args`, `response_preview`, tokens e custo. A aba JSON so existe
 * quando o servidor diz que pode.
 */
const props = defineProps({
  conversationId: { type: Number, required: true },
  messageCreatedAt: { type: Number, default: null },
  open: { type: Boolean, default: false },
});

const emit = defineEmits(['close']);
const { t } = useI18n();

const carregando = ref(false);
const erro = ref('');
const agente = ref(null);
const superAdmin = ref(false);
const turnos = ref([]);
const aba = ref('legivel');
const turnoAberto = ref(null);
const copiado = ref('');

// O ultimo turno que comecou ANTES desta resposta. Sem `messageCreatedAt` cai no mais recente,
// que e o certo quando o painel e aberto sem contexto de mensagem.
const turnoProvavel = computed(() => {
  if (!turnos.value.length) return null;
  if (!props.messageCreatedAt) return turnos.value[0];

  const limite = props.messageCreatedAt * 1000;
  const candidatos = turnos.value.filter(
    turno => turno.started_at && Date.parse(turno.started_at) <= limite
  );

  return candidatos.length ? candidatos[0] : turnos.value[0];
});

// Mapa explicito em vez de montar a chave de i18n por interpolacao: chave que so existe em
// tempo de execucao nao aparece num grep, e e assim que traducao vira string faltando.
const ABAS = {
  legivel: 'POWER_AGENTS.DEBUG.TAB_LEGIVEL',
  fontes: 'POWER_AGENTS.DEBUG.TAB_FONTES',
  json: 'POWER_AGENTS.DEBUG.TAB_JSON',
};

// Fontes e aba propria, e nao um pedaco da legivel, porque a pergunta que ela responde e
// outra: "de onde ele tirou isso". Ela vale para TODO administrador — `filename`,
// `source_url` e `score` sao diagnostico da base, nao corpo de API de terceiro.
const abas = computed(() =>
  superAdmin.value ? ['legivel', 'fontes', 'json'] : ['legivel', 'fontes']
);

async function buscar() {
  carregando.value = true;
  erro.value = '';
  try {
    const { data } = await ConversationApi.agentTrace(props.conversationId);
    agente.value = data.agent;
    superAdmin.value = Boolean(data.super_admin);
    // Mais recente primeiro: quem abre o painel quase sempre quer a ultima resposta.
    turnos.value = [...(data.turns || [])].sort((a, b) => b.id - a.id);
    turnoAberto.value = turnoProvavel.value?.id ?? null;
    if (!superAdmin.value) aba.value = 'legivel';
  } catch (e) {
    // 403 nao e falha: e o portao de administrador do proprio endpoint, e dizer "erro ao
    // carregar" mandaria o operador procurar defeito onde ha permissao.
    erro.value =
      e.response?.status === 403
        ? t('POWER_AGENTS.DEBUG.FORBIDDEN')
        : e.response?.data?.error || t('POWER_AGENTS.DEBUG.ERROR_LOAD');
  } finally {
    carregando.value = false;
  }
}

// `immediate` NAO e detalhe: o pai monta este componente com `v-if`, entao quando ele nasce
// `open` ja e `true` e o watch nunca veria a transicao. Sem isto o painel abre, nao busca
// nada, e mostra "esta conversa nao e de nenhum agente" — que e o valor INICIAL de `agente`,
// nao uma resposta do servidor. Os dois estados sao indistinguiveis na tela, e foi assim que
// o painel anterior passou meses parecendo quebrado por outro motivo.
watch(
  () => props.open,
  aberto => {
    if (aberto) buscar();
  },
  { immediate: true }
);

function alternarTurno(id) {
  turnoAberto.value = turnoAberto.value === id ? null : id;
}

async function copiar(texto, marca) {
  await navigator.clipboard.writeText(texto);
  copiado.value = marca;
  setTimeout(() => {
    copiado.value = '';
  }, 1500);
}

const formatarJson = valor => JSON.stringify(valor, null, 2);

const horario = iso =>
  iso ? new Date(iso).toLocaleTimeString('pt-BR', { hour12: false }) : '—';

const segundos = ms => (ms == null ? '—' : `${(ms / 1000).toFixed(2)} s`);

// Nome de coluna, protocolo, unidade e simbolo. Nao sao copy — traduzir `args` quebraria o
// proprio ponto do painel, que e mostrar o que esta gravado. Ficam como constante em vez de
// literal solto no template porque o lint (com razao) nao distingue os dois casos sozinho.
const SEPARADOR = ' · ';

const ROTULOS = {
  args: 'args',
  previa: 'response_preview',
  nulo: 'null',
  http: 'HTTP',
  ms: 'ms',
  moeda: 'US$',
};
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-n-slate-12/50"
      @click.self="emit('close')"
    >
      <div
        class="flex flex-col w-full max-w-3xl max-h-[85vh] border rounded-xl shadow-2xl bg-n-solid-1 border-n-weak"
      >
        <div
          class="flex items-start justify-between gap-4 px-5 py-4 border-b border-n-weak"
        >
          <div class="flex flex-col gap-1 min-w-0">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ t('POWER_AGENTS.DEBUG.TITLE') }}
            </h3>
            <p class="text-xs truncate text-n-slate-11">
              <span v-if="agente">{{ agente.name }}</span>
              <span v-if="agente && turnos.length">{{ SEPARADOR }}</span>
              <span v-if="turnos.length">
                {{ t('POWER_AGENTS.DEBUG.TURNS', { n: turnos.length }) }}
              </span>
            </p>
          </div>
          <button
            class="flex items-center justify-center rounded size-7 text-n-slate-11 hover:text-n-slate-12 hover:bg-n-alpha-2"
            :aria-label="t('POWER_AGENTS.DEBUG.CLOSE')"
            @click="emit('close')"
          >
            <i class="i-lucide-x text-base" />
          </button>
        </div>

        <div
          v-if="abas.length > 1"
          class="flex gap-4 px-5 border-b border-n-weak"
        >
          <button
            v-for="nome in abas"
            :key="nome"
            class="pb-2 pt-2.5 text-xs border-b-2 transition-colors"
            :class="
              aba === nome
                ? 'border-n-iris-9 text-n-iris-11 font-medium'
                : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
            "
            @click="aba = nome"
          >
            {{ t(ABAS[nome]) }}
          </button>
        </div>

        <div class="flex-1 p-5 overflow-y-auto">
          <p v-if="carregando" class="py-8 text-sm text-center text-n-slate-11">
            {{ t('POWER_AGENTS.DEBUG.LOADING') }}
          </p>

          <p v-else-if="erro" class="py-8 text-sm text-center text-n-ruby-11">
            {{ erro }}
          </p>

          <p
            v-else-if="!agente"
            class="py-8 text-sm text-center text-n-slate-11"
          >
            {{ t('POWER_AGENTS.DEBUG.NO_AGENT') }}
          </p>

          <p
            v-else-if="!turnos.length"
            class="py-8 text-sm text-center text-n-slate-11"
          >
            {{ t('POWER_AGENTS.DEBUG.NO_TURNS') }}
          </p>

          <div v-else class="flex flex-col gap-2">
            <div
              v-for="turno in turnos"
              :key="turno.id"
              class="border rounded-lg border-n-weak"
              :class="
                turno.id === turnoProvavel?.id
                  ? 'border-n-slate-7 bg-n-alpha-1'
                  : ''
              "
            >
              <button
                class="flex flex-wrap items-center w-full gap-2 px-3 py-2.5 text-left"
                @click="alternarTurno(turno.id)"
              >
                <span
                  class="font-mono text-[11px] text-n-slate-11 w-14 shrink-0"
                >
                  {{ horario(turno.started_at) }}
                </span>
                <span
                  class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium"
                  :class="
                    turno.status === 'ok'
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-ruby-3 text-n-ruby-11'
                  "
                >
                  {{ turno.status }}
                </span>
                <!-- A ferramenta quebrada dentro de um turno `ok` e o caso que o painel existe
                     para pegar: medido em producao, o turno fica verde e ninguem ve. -->
                <span
                  v-if="turno.tool_calls.some(c => c.status === 'error')"
                  class="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-n-ruby-3 text-n-ruby-11"
                >
                  <i class="i-lucide-circle-x" />
                  {{ t('POWER_AGENTS.DEBUG.TOOL_FAILED') }}
                </span>
                <span
                  v-if="turno.id === turnoProvavel?.id"
                  class="px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-n-slate-12 text-n-slate-1"
                >
                  {{ t('POWER_AGENTS.DEBUG.THIS_RESPONSE') }}
                </span>
                <!-- Só para super admin: `TraceProjection` não manda o modelo aos outros. -->
                <code
                  v-if="turno.model"
                  class="font-mono text-[11px] text-n-slate-10 truncate"
                >
                  {{ turno.model }}
                </code>
                <span class="ml-auto text-xs text-n-slate-11">
                  {{ segundos(turno.latency_ms) }}
                </span>
                <i
                  class="text-sm text-n-slate-10"
                  :class="
                    turnoAberto === turno.id
                      ? 'i-lucide-chevron-up'
                      : 'i-lucide-chevron-down'
                  "
                />
              </button>

              <div
                v-if="turnoAberto === turno.id"
                class="flex flex-col gap-3 px-3 pb-3 border-t border-n-weak pt-3"
              >
                <p v-if="turno.error" class="text-xs text-n-ruby-11">
                  {{ turno.error }}
                </p>
                <p v-if="turno.guard_note" class="text-xs text-n-amber-11">
                  {{ turno.guard_note }}
                </p>

                <div
                  v-if="superAdmin && turno.cost_usd != null"
                  class="flex flex-wrap gap-x-4 gap-y-1 px-3 py-2 rounded-lg bg-n-alpha-1"
                >
                  <span class="text-[11px] text-n-slate-11">
                    {{ t('POWER_AGENTS.DEBUG.TOKENS_IN') }}
                    <span class="font-mono text-n-slate-12">{{
                      turno.tokens_in
                    }}</span>
                  </span>
                  <span class="text-[11px] text-n-slate-11">
                    {{ t('POWER_AGENTS.DEBUG.TOKENS_OUT') }}
                    <span class="font-mono text-n-slate-12">{{
                      turno.tokens_out
                    }}</span>
                  </span>
                  <span class="text-[11px] text-n-slate-11">
                    {{ t('POWER_AGENTS.DEBUG.CACHE') }}
                    <span class="font-mono text-n-teal-11">{{
                      turno.cache_read
                    }}</span>
                  </span>
                  <span class="text-[11px] text-n-slate-11 ml-auto">
                    {{ t('POWER_AGENTS.DEBUG.COST') }}
                    <span class="font-mono text-n-slate-12">
                      {{ ROTULOS.moeda }} {{ turno.cost_usd?.toFixed(5) }}
                    </span>
                  </span>
                </div>

                <p
                  v-if="!turno.tool_calls.length"
                  class="text-xs text-n-slate-10"
                >
                  {{ t('POWER_AGENTS.DEBUG.NO_TOOLS') }}
                </p>

                <div
                  v-for="(chamada, i) in turno.tool_calls"
                  :key="`${turno.id}-${i}`"
                  class="flex flex-col gap-2 p-3 border rounded-lg"
                  :class="
                    chamada.status === 'error'
                      ? 'border-n-ruby-6 bg-n-ruby-2'
                      : 'border-n-weak bg-n-background'
                  "
                >
                  <div class="flex flex-wrap items-center gap-2">
                    <i class="text-xs i-lucide-wrench text-n-slate-10" />
                    <code class="font-mono text-xs text-n-slate-12">
                      {{ chamada.tool_name }}
                    </code>
                    <span
                      class="px-1.5 py-0.5 rounded text-[10px] font-medium"
                      :class="
                        chamada.status === 'error'
                          ? 'bg-n-ruby-3 text-n-ruby-11'
                          : 'bg-n-teal-3 text-n-teal-11'
                      "
                    >
                      {{ chamada.status }}
                    </span>
                    <!-- `http_status` nulo com latencia de milissegundos e assinatura de
                         chamada que nunca saiu do processo. Mostrar o campo vazio, e nao
                         esconde-lo, e o que deixa isso legivel. -->
                    <span class="text-[11px] text-n-slate-10">
                      {{ ROTULOS.http }} {{ chamada.http_status ?? '—' }}
                    </span>
                    <span class="ml-auto text-[11px] text-n-slate-10">
                      {{ chamada.latency_ms }} {{ ROTULOS.ms }}
                    </span>
                  </div>

                  <p
                    v-if="chamada.error"
                    class="font-mono text-xs text-n-ruby-11"
                  >
                    {{ chamada.error }}
                  </p>

                  <template v-if="aba === 'fontes'">
                    <div
                      v-if="chamada.resumo.fontes"
                      class="flex flex-col gap-2"
                    >
                      <div class="flex flex-wrap items-center gap-2">
                        <span
                          v-for="base in chamada.resumo.fontes.bases"
                          :key="base.id"
                          class="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-md bg-n-alpha-1 text-n-slate-11"
                        >
                          <i class="i-lucide-database" />
                          {{ base.name }}
                        </span>
                        <span
                          v-if="chamada.resumo.fontes.top_score != null"
                          class="ml-auto text-[11px] text-n-slate-10"
                        >
                          {{
                            t('POWER_AGENTS.DEBUG.TOP_SCORE', {
                              s: chamada.resumo.fontes.top_score.toFixed(3),
                            })
                          }}
                        </span>
                      </div>

                      <!-- Sem base conectada, buscou e nao achou, e achou sao TRES estados que
                           o HTTP 200 nao separa. A tela separa. -->
                      <p
                        v-if="!chamada.resumo.fontes.bases.length"
                        class="text-xs text-n-amber-11"
                      >
                        {{ t('POWER_AGENTS.DEBUG.KB_NO_BASE') }}
                      </p>
                      <p
                        v-else-if="!chamada.resumo.fontes.itens.length"
                        class="text-xs text-n-slate-10"
                      >
                        {{ t('POWER_AGENTS.DEBUG.KB_EMPTY') }}
                      </p>

                      <a
                        v-for="fonte in chamada.resumo.fontes.itens"
                        :key="fonte.chunk_id"
                        :href="fonte.source_url || undefined"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="flex flex-col gap-1 p-2.5 border rounded-lg border-n-weak hover:border-n-slate-7"
                      >
                        <div class="flex items-start gap-2">
                          <span
                            class="flex-1 text-xs leading-snug text-n-slate-12"
                            >{{ fonte.filename }}</span
                          >
                          <span
                            class="font-mono text-[11px] shrink-0"
                            :class="
                              fonte.score >= 0.7
                                ? 'text-n-teal-11'
                                : 'text-n-amber-11'
                            "
                            >{{ fonte.score?.toFixed(3) }}</span
                          >
                        </div>
                        <span
                          v-if="fonte.source_url"
                          class="text-[11px] truncate text-n-slate-10"
                          >{{ fonte.source_url }}</span
                        >
                      </a>

                      <!-- Dizer quantas o RASTRO nao guardou e o que impede alguem de ler
                           "trouxe 3" e concluir que a base esta pobre. -->
                      <p
                        v-if="chamada.resumo.fontes.ocultas"
                        class="text-[11px] text-n-slate-10"
                      >
                        {{
                          t('POWER_AGENTS.DEBUG.SOURCES_HIDDEN', {
                            n: chamada.resumo.fontes.ocultas,
                          })
                        }}
                      </p>
                    </div>
                    <p v-else class="text-xs text-n-slate-10">
                      {{ t('POWER_AGENTS.DEBUG.NO_SOURCES') }}
                    </p>
                  </template>

                  <template v-if="aba === 'legivel'">
                    <div
                      v-if="chamada.resumo.parametros.length"
                      class="flex flex-wrap gap-1.5"
                    >
                      <span
                        v-for="param in chamada.resumo.parametros"
                        :key="param.chave"
                        class="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs border rounded-md border-n-weak text-n-slate-11"
                      >
                        {{ param.chave }}
                        <code class="font-mono text-n-slate-12">
                          {{ param.valor }}
                        </code>
                      </span>
                    </div>
                    <p
                      v-if="chamada.resumo.omitidos.length"
                      class="text-[11px] text-n-slate-10"
                    >
                      {{
                        t('POWER_AGENTS.DEBUG.OMITTED', {
                          n: chamada.resumo.omitidos.length,
                        })
                      }}
                    </p>

                    <div
                      v-if="chamada.resumo.retorno"
                      class="flex flex-wrap gap-1.5"
                    >
                      <span
                        v-for="campo in chamada.resumo.retorno.campos || []"
                        :key="campo.chave"
                        class="inline-flex items-center gap-1.5 px-2 py-0.5 text-xs rounded-md bg-n-alpha-1 text-n-slate-11"
                      >
                        {{ campo.chave }}
                        <span class="font-mono text-n-slate-12">
                          {{
                            campo.tipo === 'escalar' ? campo.valor : campo.itens
                          }}
                        </span>
                      </span>
                    </div>
                    <!-- Sem previa e diferente de previa vazia: a ferramenta caiu antes de
                         receber resposta. -->
                    <p v-else class="text-[11px] text-n-slate-10">
                      {{ t('POWER_AGENTS.DEBUG.NO_RETURN') }}
                    </p>
                  </template>

                  <template v-else-if="aba === 'json'">
                    <div class="flex flex-col gap-1">
                      <div class="flex items-center gap-2">
                        <span class="font-mono text-[11px] text-n-slate-12">{{
                          ROTULOS.args
                        }}</span>
                        <button
                          class="ml-auto text-[11px] text-n-iris-11"
                          @click="
                            copiar(
                              formatarJson(chamada.args),
                              `a${turno.id}-${i}`
                            )
                          "
                        >
                          {{
                            copiado === `a${turno.id}-${i}`
                              ? t('POWER_AGENTS.DEBUG.COPIED')
                              : t('POWER_AGENTS.DEBUG.COPY')
                          }}
                        </button>
                      </div>
                      <pre
                        class="p-3 overflow-x-auto font-mono text-[11px] leading-relaxed border rounded-lg border-n-weak bg-n-solid-1 text-n-slate-11"
                        >{{ formatarJson(chamada.args) }}</pre>
                    </div>

                    <div class="flex flex-col gap-1">
                      <div class="flex items-center gap-2">
                        <span class="font-mono text-[11px] text-n-slate-12">
                          {{ ROTULOS.previa }}
                        </span>
                        <button
                          v-if="chamada.response_preview"
                          class="ml-auto text-[11px] text-n-iris-11"
                          @click="
                            copiar(
                              chamada.response_preview,
                              `r${turno.id}-${i}`
                            )
                          "
                        >
                          {{
                            copiado === `r${turno.id}-${i}`
                              ? t('POWER_AGENTS.DEBUG.COPIED')
                              : t('POWER_AGENTS.DEBUG.COPY')
                          }}
                        </button>
                      </div>
                      <!-- Sem altura maxima de proposito: a previa inteira rola junto com o
                           painel, e so a linha longa rola de lado. `response_preview` chega
                           como STRING de JSON; a leitura e a mesma do Playground. -->
                      <pre
                        v-if="chamada.response_preview"
                        class="p-3 overflow-x-auto font-mono text-[11px] leading-relaxed border rounded-lg border-n-weak bg-n-solid-1 text-n-slate-11"
                        data-resposta-da-tool
                        >{{ previaIndentada(chamada.response_preview) }}</pre>
                      <p v-else class="font-mono text-[11px] text-n-slate-10">
                        {{ ROTULOS.nulo }}
                      </p>
                    </div>
                  </template>
                </div>
              </div>
            </div>

            <!-- Dito uma vez, no rodape: o painel abre num turno escolhido por HORARIO, nao
                 por vinculo. Quem precisar conferir tem os outros turnos logo acima. -->
            <p class="pt-1 text-[11px] text-n-slate-10">
              {{ t('POWER_AGENTS.DEBUG.MATCH_BY_TIME') }}
            </p>
            <p v-if="aba === 'json'" class="text-[11px] text-n-slate-10">
              {{ t('POWER_AGENTS.DEBUG.ELISION_NOTE') }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
